import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useAuth } from '../../auth/AuthContext';
import { apiClient } from '../../services/apiClient';
import { useRoleTranslation } from '../../hooks/useRoleTranslation';
import TermsModal from '../Layout/TermsModal';

const languages = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'it', name: 'Italiano' }
];

export default function EmployeeSettings() {
  const { t, i18n } = useTranslation();
  const { getRoleLabel } = useRoleTranslation();
  const { user, updateUserPreferences } = useAuth();
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [languageError, setLanguageError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [formData, setFormData] = useState({
    medecin: '',
    employee_name: '',
    signature_image: '',
    signature_image_mime: ''
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await apiClient.get('/api/profile');
        setFormData({
          medecin: userData.medecin ?? '',
          employee_name: userData.employee_name ?? '',
          signature_image: userData.signature_image ?? '',
          signature_image_mime: userData.signature_image_mime ?? ''
        });
      } catch (err) {
        console.error('Error fetching user data:', err);
        setFormError(t('settings.profile.error.fetch'));
      }
    };

    fetchUserData();
  }, [t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setFormError(null);
      setFormSuccess(false);

      await apiClient.patch('/api/profile', {
        medecin: formData.medecin,
        employee_name: formData.employee_name,
        signature_image: formData.signature_image || null,
        signature_image_mime: formData.signature_image_mime || null
      });

      setFormSuccess(true);
    } catch (err) {
      console.error('Error updating profile:', err);
      setFormError(t('settings.profile.error.save'));
    } finally {
      setSaving(false);
    }
  };

  const handleSignatureFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result; // data URL
      setFormData(prev => ({
        ...prev,
        signature_image: result,
        signature_image_mime: file.type
      }));
    };
    reader.readAsDataURL(file);
  };

  // Get translated role
  const getTranslatedRole = (role) => {
    if (!role) return t('header.noGroupAssigned');
    return getRoleLabel(`role-${role.toLowerCase()}`);
  };

  const handleLanguageChange = async (languageCode) => {
    try {
      setLanguageError(null);
      
      // Change language in i18n first
      i18n.changeLanguage(languageCode);
      
      if (user) {
        // Update preferences in the backend
        await updateUserPreferences({
          ...user.preferences,
          language: languageCode
        });
      }

      // Save to localStorage as fallback
      localStorage.setItem('preferredLanguage', languageCode);
    } catch (err) {
      console.error('Error saving language preference:', err);
      // Revert language change if save failed
      i18n.changeLanguage(user?.preferences?.language || 'en');
      setLanguageError(t('settings.language.error.save'));
    }
  };

  return (
    <div className="bg-gray-100 rounded-lg">
      <div className="px-6 py-4 flex items-center justify-between gap-4">
        <h2 className="text-lg font-medium text-gray-900">
          {t('settings.profile.title')}
        </h2>
        <button
          type="button"
          onClick={() => setIsTermsOpen(true)}
          className="text-sm text-gray-500 hover:text-gray-700 underline underline-offset-2 shrink-0"
        >
          {t('terms.viewLink')}
        </button>
      </div>

      <div className="px-6 py-4">
        {/* Email and Role */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings.profile.email')}
            </label>
            <p className="text-sm text-gray-900 bg-gray-200 px-3 py-2 rounded-md">
              {user?.email}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings.profile.role')}
            </label>
            <p className="text-sm text-gray-900 bg-gray-200 px-3 py-2 rounded-md">
              {getTranslatedRole(user?.group)}
            </p>
          </div>
        </div>

        {/* Employee Name and Medecin */}
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <label htmlFor="medecin" className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings.profile.medecin')}
            </label>
            <input
              type="text"
              id="medecin"
              name="medecin"
              value={formData.medecin}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="employee_name" className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings.profile.employee_name')}
            </label>
            <input
              type="text"
              id="employee_name"
              name="employee_name"
              value={formData.employee_name}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>

        {/* Signature Upload */}
        <div className="mt-6 grid grid-cols-2 gap-4 items-start">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings.profile.signature', 'Signature')}
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleSignatureFile}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">{t('settings.profile.signatureHint', 'PNG/JPG')}</p>
          </div>
          <div className="flex items-center justify-center">
            {formData.signature_image ? (
              <img
                src={formData.signature_image}
                alt="signature preview"
                className="max-h-24 max-w-full border rounded"
              />
            ) : (
              <div className="text-sm text-gray-500">{t('settings.profile.noSignature', 'No image uploaded')}</div>
            )}
          </div>
        </div>

        {/* Language Selection */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('settings.language.select')}
          </label>
          <Menu as="div" className="relative inline-block text-left">
            {({ open }) => (
              <>
                <Menu.Button className="inline-flex w-56 justify-between items-center rounded-md bg-[rgb(197,214,248)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(187,204,238)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                  {languages.find(lang => lang.code === i18n.language)?.name}
                  <ChevronDownIcon 
                    className={`ml-2 h-5 w-5 text-gray-500 transition-transform duration-200 ${
                      open ? 'transform rotate-180' : ''
                    }`} 
                    aria-hidden="true" 
                  />
                </Menu.Button>

                <Menu.Items className="absolute left-0 z-10 mt-2 w-56 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="py-1">
                    {languages.map((language) => (
                      <Menu.Item key={language.code}>
                        {({ active }) => (
                          <button
                            onClick={() => handleLanguageChange(language.code)}
                            className={`${
                              active ? 'bg-[rgb(197,214,248)]' : ''
                            } ${
                              i18n.language === language.code ? 'bg-[rgb(197,214,248)]' : ''
                            } group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                          >
                            {language.name}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </>
            )}
          </Menu>
          {languageError && (
            <div className="mt-2 text-sm text-red-600">
              {languageError}
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className={`inline-flex justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 
              ${!saving ? 'hover:bg-[rgb(121,192,189)]' : 'opacity-75 cursor-not-allowed'}
              focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>

        {formError && (
          <div className="mt-4 p-4 rounded-md bg-red-50 text-sm text-red-700">
            {formError}
          </div>
        )}

        {formSuccess && (
          <div className="mt-4 p-4 rounded-md bg-green-50 text-sm text-green-700">
            {t('settings.profile.success')}
          </div>
        )}
      </div>

      <TermsModal
        isOpen={isTermsOpen}
        onClose={() => setIsTermsOpen(false)}
        mode="view"
      />
    </div>
  );
} 